import { appInsightsService } from 'app/utils/constants';

/**
 * Shared telemetry helper for the ticket-creation wizards (Flow B = createTicket,
 * Flow A = addCustomer). Holds a single `flowInstanceId` per flow attempt plus the
 * active `flow` tag, and normalises all property values to strings before emitting
 * through the existing `appInsightsService.logRequestflowTelemetry` pipeline.
 */
export type FlowName = 'createTicket' | 'addCustomer';

export type TelemetryValue = string | number | boolean | null | undefined;

let flowInstanceId = '';
let currentFlow: FlowName = 'createTicket';
let flowStartMs = 0;
let flowEnded = false;

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const generateId = (): string => {
    const c = typeof crypto !== 'undefined' ? (crypto as { randomUUID?: () => string }) : undefined;
    if (c && typeof c.randomUUID === 'function') {
        return c.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Start a flow or resume its saved correlation, recording time for this session. */
export const startFlow = (flow: FlowName, savedInstanceId?: string): string => {
    flowInstanceId = savedInstanceId || generateId();
    currentFlow = flow;
    flowStartMs = now();
    flowEnded = false;
    return flowInstanceId;
};

/** Mark the flow as intentionally ended (submitted or cancelled) so unmount does not log an abandon. */
export const endFlow = (): void => {
    flowEnded = true;
};

/** True once the flow has been submitted or cancelled. */
export const isFlowEnded = (): boolean => flowEnded;

/** Seconds elapsed since the current flow started (for totalFlowDuration / time-on-step). */
export const flowElapsedSeconds = (): number => Math.max(0, Math.round((now() - flowStartMs) / 1000));

export const getFlowInstanceId = (): string => flowInstanceId;

/**
 * Internal: build string props, dev-log, and emit through the existing pipeline. Never throws.
 */
const emitEvent = (
    action: string,
    section: string,
    baseProps: { [key: string]: string },
    properties: { [key: string]: TelemetryValue }
): void => {
    try {
        const stringProps: { [key: string]: string } = { ...baseProps };
        Object.keys(properties).forEach((key) => {
            const value = properties[key];
            if (value !== null && value !== undefined) {
                stringProps[key] = String(value);
            }
        });
        appInsightsService.logRequestflowTelemetry(action, section, stringProps);
    } catch {
        // Swallow telemetry errors — they must never affect the user experience.
    }
};

/**
 * Emit a wizard-flow event, stamping `flowInstanceId` + `flow` and stringifying every value.
 * Never throws — telemetry must not break the UI.
 */
export const logFlowEvent = (
    action: string,
    section: string,
    properties: { [key: string]: TelemetryValue } = {}
): void => {
    emitEvent(action, section, { flowInstanceId, flow: currentFlow }, properties);
};

/**
 * Emit an Entry Page event (catalog category 3). These occur on the landing/search page BEFORE
 * a flow starts, so they carry `flow: 'entry'` instead of a flowInstanceId.
 */
export const logEntryEvent = (
    action: string,
    properties: { [key: string]: TelemetryValue } = {}
): void => {
    emitEvent(action, 'Entry Page', { flow: 'entry' }, properties);
};
